import React, { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import { parseSnappyPDF, extractSnappyFields, parseOneStopPDF } from "../utils/pdfParser";
import "../pages/ZReport.css";
import { toast } from "react-toastify";

export default function ZReportAdvanced() {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [oneStopFile, setOneStopFile] = useState(null);
  const [snappyFile, setSnappyFile] = useState(null);
  const [snappyParsedFields, setSnappyParsedFields] = useState(null);
  const [oneStopParsedFields, setOneStopParsedFields] = useState(null);
  const [transactionDay, setTransactionDay] = useState(null);
  const [errors, setErrors] = useState({});
  const [allowOverwrite, setAllowOverwrite] = useState(false);

  // Parse Snappy PDF
  const parseAndFillSnappy = async (file) => {
    try {
      const text = await parseSnappyPDF(file);
      const fields = extractSnappyFields(text);
      setSnappyParsedFields(fields);
    } catch (err) {
      toast.error("Failed to parse Snappy PDF");
    }
  };

  // Parse One Stop PDF
  const parseAndFillOneStop = async (file) => {
    try {
      const fields = await parseOneStopPDF(file, date);
      setOneStopParsedFields(fields);
      setTransactionDay(fields.transactionDay);
    } catch (err) {
      toast.error("Failed to parse One Stop PDF");
    }
  };

  // File upload handlers
  const handleSnappyUpload = async (e) => {
    const file = e.target.files[0];
    setSnappyFile(file);
    await parseAndFillSnappy(file);
  };
  const handleOneStopUpload = async (e) => {
    const file = e.target.files[0];
    setOneStopFile(file);
    await parseAndFillOneStop(file);
  };

  useEffect(() => {
    if (oneStopFile) {
      parseAndFillOneStop(oneStopFile);
    }
    // eslint-disable-next-line
  }, [date]);

  // --- Validation ---
  const validate = () => {
    const newErrors = {};
    if (!oneStopParsedFields) newErrors.oneStopFile = "One Stop PDF is required.";
    if (!snappyParsedFields) newErrors.snappyFile = "Snappy PDF is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // --- Helpers for DB ---
  const getCurrentUser = async () => {
    const { data, error } = await supabase.auth.getUser();
    if (error) throw new Error("Auth error: " + error.message);
    return data.user?.user_metadata?.display_name || data.user?.email || "Unknown";
  };

  // ---------- ONE STOP SAVE (insert + upsert, full mapping & safe columns) ----------
  const saveOneStopExtract = async (fields, date, overwrite, uploadedBy) => {
    // Build payload carefully: only columns that exist in the (new) table schema.
    const payload = {
      report_date: date,
      store_name: fields.storeName,             // may be undefined -> stored as null
      z_reading_total: fields.totalTillSales,   // total till sales (Z reading)
      till_variance: fields.tillVariance,

      // category sales
      news_sales: fields.news,
      tobacco_sales: fields.tobacco,
      offlicence_sales: fields.offLicence,
      lotto_sales: fields.lottery,

      // tenders
      cash_sales: fields.cashSales,
      card_sales: fields.cardSalesAfterRefunds, // aggregated card total
      cheque_sales: fields.cheque,
      amex_sales: fields.amex,
      mastercard: fields.mastercard,
      visa: fields.visa,
      visa_debit: fields.visaDebit,
      debit_mastercard: fields.debitMastercard,
      vouchers: fields.vouchers,
      news_account: fields.newsAccount,
      just_eat: fields.justEat,
      deliveroo: fields.deliveroo,

      // meta
      uploaded_by: uploadedBy,
    };

    if (!overwrite) {
      const { data } = await supabase
        .from("onestop_extracted")
        .select("id")
        .eq("report_date", date)
        .maybeSingle();
      if (data) throw new Error("❌ OneStop extract already exists for " + date);

      const { error } = await supabase.from("onestop_extracted").insert(payload);
      if (error) throw new Error("OneStop save failed: " + error.message);
    } else {
      const { error } = await supabase.from("onestop_extracted").upsert(payload);
      if (error) throw new Error("OneStop upsert failed: " + error.message);
    }
    toast.success("✅ One Stop extract saved");
  };

  // ---------- SNAPPY SAVE (insert + upsert, full mapping) ----------
  const saveSnappyExtract = async (fields, date, overwrite, uploadedBy) => {
    const payload = {
      report_date: date,

      // sales
      total_product_sales: fields.totalProductSales,
      net_sales: fields.netSales,

      // discounts
      multi_buy_discounts: fields.multiBuyDiscounts,
      coupon_discounts: fields.couponDiscounts,
      loyalty_discounts: fields.loyaltyDiscounts,
      total_discounts: fields.totalDiscounts,
      snappy_funded_discounts: fields.snappyFundedDiscounts,

      // surcharges
      delivery_charge: fields.deliveryCharge,
      driver_tips: fields.driverTips,
      bag_charge: fields.bagCharge,
      other_surcharges: fields.otherSurcharges,
      total_surcharges: fields.totalSurcharges,

      // refunds
      refunds: fields.refunds,

      // payments
      card_sales_before_refunds: fields.CardSalesbeforeRefunds,
      card_sales: fields.CardSalesafterRefunds, // after refunds
      cash_sales: fields.cashSales,
      snappy_customer_fee: fields.SnappycustomerFeeonCashSales,

      // retailer fees
      retailer_charge: fields.retailerCharge,
      retailer_epos_charge: fields.retailerEposCharge,
      vat: fields.Vat,
      total_retailer_fee: fields.totalRetailerFee,

      // meta
      uploaded_by: uploadedBy,
    };

    if (!overwrite) {
      const { data } = await supabase
        .from("snappy_extracted")
        .select("id")
        .eq("report_date", date)
        .maybeSingle();
      if (data) throw new Error("❌ Snappy extract already exists for " + date);

      const { error } = await supabase.from("snappy_extracted").insert(payload);
      if (error) throw new Error("Snappy save failed: " + error.message);
    } else {
      const { error } = await supabase.from("snappy_extracted").upsert(payload);
      if (error) throw new Error("Snappy upsert failed: " + error.message);
    }
    toast.success("✅ Snappy extract saved");
  };

  // --- Save button ---
  const saveExtracts = async () => {
    if (!validate()) return;

    const snappyDate = snappyParsedFields?.snappyReportDate;
    const oneStopDate = date; // we rely on date picker

    if (!snappyDate || snappyDate !== date) {
      toast.error("❌ Snappy report date does not match the selected date.");
      return;
    }
    if (!oneStopParsedFields) {
      toast.error("❌ One Stop data missing.");
      return;
    }
    if (snappyDate !== oneStopDate) {
      toast.error(`❌ Date mismatch between Snappy (${snappyDate}) and OneStop (${oneStopDate}).`);
      return;
    }

    try {
      const uploadedBy = await getCurrentUser();

      if (oneStopParsedFields) {
        await saveOneStopExtract(oneStopParsedFields, date, allowOverwrite, uploadedBy);
      }
      if (snappyParsedFields) {
        await saveSnappyExtract(snappyParsedFields, date, allowOverwrite, uploadedBy);
      }

      toast.success("✅ Extracts saved successfully!");
    } catch (err) {
      toast.error(err.message || "Save failed");
    }
  };

  // For display
  const selectedDateObj = new Date(date);
  const selectedDateDisplay = selectedDateObj.toLocaleDateString("en-GB");

  // OneStop card breakdown
  const amex = oneStopParsedFields?.amex || 0;
  const mastercard = oneStopParsedFields?.mastercard || 0;
  const visa = oneStopParsedFields?.visa || 0;
  const visaDebit = oneStopParsedFields?.visaDebit || 0;
  const debitMastercard = oneStopParsedFields?.debitMastercard || 0;
  const totalCardSales =
    amex + mastercard + visa + visaDebit + debitMastercard;

  return (
    <div className="zreport-container">
      <h2>ZReport Uploader</h2>

      <label>Date:</label>
      <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />

      <label>Upload Snappy PDF:</label>
      <input type="file" accept="application/pdf" onChange={handleSnappyUpload} />
      {errors.snappyFile && <div className="error">{errors.snappyFile}</div>}
      {snappyParsedFields?.snappyReportDate && snappyParsedFields.snappyReportDate !== date && (
        <div className="error">
          ⚠️ Snappy report date ({snappyParsedFields.snappyReportDate}) does not match selected date ({date})
        </div>
      )}

      <label>Upload One Stop PDF:</label>
      <input type="file" accept="application/pdf" onChange={handleOneStopUpload} />
      {errors.oneStopFile && <div className="error">{errors.oneStopFile}</div>}

      <div className="form-section">
        <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <input
            type="checkbox"
            checked={allowOverwrite}
            onChange={(e) => setAllowOverwrite(e.target.checked)}
          />
          Allow overwrite if record already exists
        </label>
      </div>

      <button onClick={saveExtracts}>Save Extracts</button>

      {/* --- Parsed Values Table at Bottom --- */}
      <div className="parsed-section">
        <h3>Parsed Values</h3>
        <div style={{ display: "flex", gap: "2rem", flexWrap: "wrap" }}>
          {snappyParsedFields && (
            <table className="parsed-table">
              <caption>Snappy Parsed Summary</caption>
              <tbody>
                {Object.entries(snappyParsedFields).map(([key, val]) => (
                  <tr key={key}>
                    <td style={{ fontWeight: "bold" }}>{key}</td>
                    <td>{val}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {oneStopParsedFields && (
            <table className="parsed-table">
              <caption>One Stop Parsed Summary</caption>
              <tbody>
                {Object.entries(oneStopParsedFields).map(([key, val]) => (
                  <tr key={key}>
                    <td style={{ fontWeight: "bold" }}>{key}</td>
                    <td>{typeof val === "boolean" ? String(val) : val}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {oneStopParsedFields && (
          <table className="parsed-table" style={{ marginTop: "2rem" }}>
            <caption>One Stop Day Summary</caption>
            <tbody>
              <tr>
                <td style={{ fontWeight: "bold" }}>
                  Total Card Sales ({selectedDateDisplay}):
                </td>
                <td>
                  £{totalCardSales.toFixed(2)} = £(
                  Amex: £{amex.toFixed(2)} + MasterCard: £{mastercard.toFixed(2)} +
                  VISA: £{visa.toFixed(2)} + Visa Debit: £{visaDebit.toFixed(2)} +
                  Debit Mastercard: £{debitMastercard.toFixed(2)})
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: "bold" }}>
                  Total Till Sales ({selectedDateDisplay}):
                </td>
                <td>
                  £{(oneStopParsedFields.totalTillSales || 0).toFixed(2)}
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: "bold" }}>
                  Total Cash Sales ({selectedDateDisplay}):
                </td>
                <td>
                  £{(oneStopParsedFields.cashSales || 0).toFixed(2)}
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: "bold" }}>
                  Snappy Sales (Cheque) ({selectedDateDisplay}):
                </td>
                <td>
                  £{(oneStopParsedFields.cheque || 0).toFixed(2)}
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: "bold" }}>
                  Vouchers ({selectedDateDisplay}):
                </td>
                <td>
                  £{(oneStopParsedFields.vouchers || 0).toFixed(2)}
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: "bold" }}>
                  Calculated Date:
                </td>
                <td>
                  <strong>{selectedDateDisplay}</strong> ({transactionDay})
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
